import { useMemo, useState, useEffect } from "react";
import { verifyMfaOtp, verifyUser } from "../Services/AuthService";
import { replace, useNavigate } from "react-router-dom";
import { startSession } from "../Services/StartSession";
import NotificationAlert from "../../CustomAlerts/NotificationAlert";
import { setAuthCookies } from "../../Utils/authUtils";

const LoginModal = ({ isOpen, onClose }) => {
  const [loginStep, setLoginStep] = useState("credentials");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [otpValue, setOtpValue] = useState("");

  const [userPayload, setUserPayload] = useState({
    email: "",
    password: "",
  });

  const [authResult, setAuthResult] = useState(null);
  const [alert, setAlert] = useState(null);

  const navigate = useNavigate();

  const welcomeName = useMemo(
    () => authResult?.userDetails?.name || "your workspace",
    [authResult]
  );

  // LOGIN
  const handleLoginChange = (e) => {
    const { name, value } = e.target;

    setUserPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCredentialSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const { data } = await verifyUser({
        email: userPayload.email,
        password: userPayload.password,
      });

      if (data?.status !== "Success") {
        setAlert({
          type: "error",
          message: data?.message,
        });

        return;
      }

      setAlert({
        type: "success",
        message: data?.message || "Login Successful",
      });

      setAuthResult(data);

      setLoginStep(
        data?.userDetails?.mfaEnabled
          ? "otp"
          : "success"
      );
    } catch (err) {
      setAlert({
        type: "error",

        message:
          err?.response?.data?.message ||
          err?.message ||
          "Login failed",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // OTP
  const handleOtpSubmit = async () => {
    if (!otpValue.trim()) {
      setErrorMessage("Please enter OTP");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const callbackUrl = `${window.location.origin}/`;

      const { data } =
        await verifyMfaOtp({
          userUID: authResult?.userDetails?.userUID,
          otp: otpValue.trim(),
          callbackURL: callbackUrl,
        });

      if (data?.status !== "Success") {
        setAlert({
          type: "error",
          message: data?.message,
        });
        return;
      }

      setAlert({
        type: "success",
        message: data?.message || "OTP Verified Successfully",
      });

      const url = new URL(data.callbackURL);

      const token = url.searchParams.get("token");

      if (!token) {
        throw new Error(
          "Token not found in callback URL"
        );
      }

      const sessionData = await startSession(userPayload.email, token);
      if(sessionData?.status === "Success" && sessionData?.isActive === false) {
        navigate("/inactive-user", { replace: true });
        return;
      }
      else {
        setAuthCookies({
          token,
          user: {
            name: sessionData.name,
            email: sessionData.email,
            isAdmin: sessionData?.isAdmin,
            isManager: sessionData?.isManager
          },          
        });
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: err?.message || "OTP verification failed",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // SCROLL LOCK
  useEffect(() => {
    document.body.style.overflow =
      isOpen
        ? "hidden"
        : "auto";

    return () => {
      document.body.style.overflow =
        "auto";
    };
  }, [isOpen]);

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] overflow-hidden transition-all duration-500
                    ${
                      isOpen
                        ? "visible pointer-events-auto"
                        : "invisible pointer-events-none"
                    }
                  `}
      >
        {/* OVERLAY */}
        <button
          onClick={onClose}
          className={`absolute inset-0 bg-slate-950/45 backdrop-blur-sm transition-opacity duration-500 ease-out
                      ${isOpen ? "opacity-100" : "opacity-0"}
                    `}
        />

        {/* PANEL */}
        <div
          className={`absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl shadow-slate-950/20 transform-gpu
                      will-change-transform transition-transform duration-[750ms] ease-[cubic-bezier(0.22,1,0.36,1)] 
                      ${isOpen ? "translate-x-0" : "translate-x-full"}
                    `}
        >
          <div className="flex h-full flex-col overflow-y-auto">

            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#0B5ED7]">
                  {loginStep === "credentials" ? "Secure Sign-In" : "Identity Verification"}
                </p>

                <h2 className="mt-2 font-display text-3xl font-extrabold text-[#0A2540]">
                  {loginStep === "credentials" ? "Access Your Opt4CRM Account" : "Complete Multi-Factor Authentication"}
                </h2>
              </div>

              <button
                onClick={onClose}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 px-6 py-8">

              <div className="rounded-[28px] bg-gradient-to-br from-[#0A2540] to-[#0B5ED7] p-6 text-white">

                <p className="text-sm font-semibold text-[#DBEAFE]">
                  {loginStep === "credentials" ? "Authorized Platform Access" : "Additional Verification Required"}
                </p>

                <p className="mt-3 text-2xl font-extrabold">
                  {loginStep === "credentials" 
                    ? "Sign in with your business email and password to continue to your CRM workspace."
                    : `Account identified for ${welcomeName}`
                  }
                </p>

              </div>

              {/* LOGIN */}
              {loginStep === "credentials" && (
                <form
                  onSubmit={handleCredentialSubmit}
                  className="mt-8 space-y-5"
                >
                  <label
                    htmlFor="login-email"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    Corporate Email Address
                  </label>
                  <input
                    name="email"
                    value={userPayload.email}
                    onChange={handleLoginChange}
                    placeholder="Email"
                    className="w-full border p-3 rounded-xl"
                  />

                  <label
                    htmlFor="login-password"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    Password
                  </label>
                  <input
                    name="password"
                    type="password"
                    value={userPayload.password}
                    onChange={handleLoginChange}
                    placeholder="Password"
                    className="w-full border p-3 rounded-xl"
                  />
                  <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] px-4 py-3 text-sm text-slate-600">
                    Your credentials are submitted through the organization
                    authentication service with multi-factor protection enabled.
                  </div>

                  <button className="w-full rounded-full bg-[#0B5ED7] py-3 font-semibold text-white shadow-[0_12px_30px_rgba(11,94,215,0.20)] transition-all duration-200 hover:bg-[#094DB1]">
                    {isSubmitting ? "Signing in..." : "Sign In"}
                  </button>
                </form>
              )}

              {/* OTP */}
              {loginStep === "otp" && (
                <div className="mt-8 space-y-5">
                  <div className="rounded-[28px] border border-slate-200 bg-[#F8FAFC] p-5">
                    <p className="text-sm font-semibold text-slate-500">
                      Verified Account
                    </p>
                    <p className="mt-2 text-xl font-extrabold text-[#0A2540]">
                      {authResult?.userDetails?.name}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                      Your credentials were validated successfully. Please
                      provide the current one-time passcode from your
                      authenticator application.
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="login-otp"
                      className="mb-2 block text-sm font-semibold text-slate-700"
                    >
                      One-Time Passcode
                    </label>
                    <input
                      id="login-otp"
                      type="text"
                      inputMode="numeric"
                      value={otpValue}
                      onChange={(event) => setOtpValue(event.target.value)}
                      placeholder="Enter your current OTP"
                      className="w-full rounded-2xl border border-slate-200 bg-[#F8FAFC] px-4 py-3.5 text-slate-900 outline-none transition focus:border-[#0B5ED7] focus:bg-white focus:ring-4 focus:ring-[#0B5ED7]/10"
                    />
                    <p className="mt-3 text-sm leading-6 text-slate-500">
                      Use the code generated by your registered authenticator
                      app. This verification step protects access to your
                      organization account.
                    </p>
                  </div>
                  <button
                    onClick={handleOtpSubmit}
                    className="w-full rounded-full bg-[#0B5ED7] py-3 font-semibold text-white shadow-[0_12px_30px_rgba(11,94,215,0.20)] transition-all duration-200 hover:bg-[#094DB1]"
                  >
                    Verify OTP
                  </button>
                </div>
              )}

              {/* SUCCESS */}
              {loginStep === "success" && (
                <div className="mt-8 space-y-6">
                  <div className="rounded-[28px] border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
                      Authentication Successful
                    </p>
                    <p className="mt-3 text-2xl font-extrabold text-[#0A2540]">
                      {welcomeName}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Your identity has been verified successfully. You may now
                      continue to the authorized Opt4CRM experience.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full rounded-full bg-[#0B5ED7] px-6 py-3.5 text-base font-bold text-white shadow-[0_12px_30px_rgba(11,94,215,0.20)] transition-all duration-200 hover:bg-[#094DB1]"
                  >
                    Close
                  </button>
                </div>
              )}

            </div>

          </div>
        </div>
      </div>

      <NotificationAlert
        show={!!alert}
        type={alert?.type}
        message={alert?.message}
        onClose={() =>
          setAlert(null)
        }
      />
    </>
  );
};

export default LoginModal;